<script setup lang="ts">
import { type TokenSearchResult } from '~/api/service/search/handler/index.service'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import { NATIVE_TOKEN } from '~/constants/token.constants'
import { searchFilterType } from '~/types/search.type'
import { INftType, ITokenType } from '~/types/token-type.type'

interface IProps {
  resultList: string[] | TokenSearchResult[]
  title: string
  keyword: string
  icon?: string
  type: searchFilterType
}

const props = defineProps<IProps>()

const splitTerm = computed(() => {
  return props.resultList.map(result => {
    const resultArray = [ result ]
    if (props.type !== searchFilterType.TOKEN) {
      result = result as string
      resultArray.push(result.substring(0, props.keyword.length), result.substring(props.keyword.length))
    }
    return resultArray
  })
})

const getPath = (result: string | TokenSearchResult) => {
  if (props.type === searchFilterType.BLOCK) {
    return routes.getBlockDetailPageWithTab(result as string)
  }
  if (props.type === searchFilterType.TRANSACTION) {
    return routes.getTransactionDetailPageWithTab(result as string)
  }
  if (props.type === searchFilterType.ADDRESS) {
    return routes.getAccountDetailPageWithTab(result as string)
  }
  if (props.type === searchFilterType.TOKEN) {
    const tokenSearchResult = result as TokenSearchResult

    switch (tokenSearchResult.TTP) {
      case ITokenType.NATIVE: 
        return routes.getTokensDetailPageWithTab(tokenSearchResult.ADDR, tokenDetailPageTab.info)
      case ITokenType.ERC20: 
        return routes.getTokensDetailPageWithTab(tokenSearchResult.ADDR)
      case ITokenType.ERC721:
        return routes.getNftDetailPageWithTab(INftType.ERC721, tokenSearchResult.ADDR)
      case ITokenType.ERC1155:
        return routes.getNftDetailPageWithTab(INftType.ERC1155, tokenSearchResult.ADDR)
      default: 
        return routes.getAccountDetailPageWithTab(tokenSearchResult.ADDR)
    }
  }
}
</script>

<template>
  <div v-if="splitTerm.length">
    <span class="font-bold text-primary">{{ title }}</span>
    <ol>
      <li
        v-for="[result, term, word] in splitTerm"
        :key="word"
        class="mt-2"
      >
        <NuxtLink
          :to="getPath(result)"
          class="flex cursor-pointer items-center gap-x-2 rounded-lg bg-primary/5 p-4 text-primary/40 transition-all duration-300 ease-[ease] hover:bg-primary/20 hover:no-underline"
        >
          <template v-if="type === searchFilterType.TOKEN">
            <img
              :src="(result as TokenSearchResult).IMG || `${IMAGE_BASE_URL}/token/default.png`"
              :alt="`${(result as TokenSearchResult).NM} image`"
              width="40"
              height="40"
              class="rounded-lg"
            >
            <div class="flex flex-col gap-y-1 overflow-x-hidden">
              <div class="flex items-center gap-x-2 truncate">
                <span class="truncate leading-4 text-primary">{{ (result as TokenSearchResult).NM }}</span>
                <Chip
                  :value="(result as TokenSearchResult).SB"
                  class="max-w-[120px] shrink-0 truncate"
                />
              </div>
              <!-- NXPC(NATIVE_TOKEN)는 Contract Address 숨김 (OR-389) -->
              <span
                v-if="(result as TokenSearchResult).ADDR.toLowerCase() !== NATIVE_TOKEN.address"
                class="text-base"
              >{{ (result as TokenSearchResult).ADDR }}</span>
            </div>
          </template>
          <template v-else>
            <FontAwesomeIcon :icon="icon" />
            <div class="truncate">
              <strong class="mr-[0.1px] font-normal text-primary">{{ term }}</strong>{{ word }}
            </div>
          </template>
          <FontAwesomeIcon
            icon="chevron-right"
            class="ml-auto text-primary"
          />
        </NuxtLink>
      </li>
    </ol>
  </div>
</template>

<style scoped>

</style>
